import { create } from 'zustand'

export type ToastTone = 'info' | 'success' | 'error' | 'offline'

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface ToastItem {
  id: string
  tone: ToastTone
  message: string
  action?: ToastAction
}

interface ToastState {
  toasts: ToastItem[]
  dismiss: (id: string) => void
}

/** Info/Success/Offline self-clear after this long; Error never does — see `push`. */
export const AUTO_DISMISS_MS = 5000

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  dismiss: (id) => {
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }))
  },
}))

/**
 * The most bars that may stack before the oldest is dropped.
 *
 * Error toasts never self-clear, so without a ceiling a screen that polls a
 * failing endpoint grows one bar per interval until the stack covers the page.
 */
const MAX_TOASTS = 4

/**
 * `crypto.randomUUID` exists only in a secure context — https, or localhost —
 * and dev opened from a phone over the LAN (http://192.168.x.x:5173) is
 * neither, so a bare call throws before a single toast ever renders.
 * `getRandomValues` has no such restriction. Mirrors `api/request-id.ts`'s
 * `newRequestId`; a toast id has no format to keep, so this skips its
 * version/variant bits.
 */
const randomId = (): string => {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  return Array.from(bytes, (n) => n.toString(16).padStart(2, '0')).join('')
}

interface Countdown {
  handle: ReturnType<typeof setTimeout>
  startedAt: number
  remaining: number
  paused: boolean
}

/** Only timed toasts have an entry; hovering one pauses its clock, leaving resumes it. */
const countdowns = new Map<string, Countdown>()

function armDismiss(id: string, ms: number) {
  const handle = setTimeout(() => {
    countdowns.delete(id)
    useToastStore.getState().dismiss(id)
  }, ms)
  countdowns.set(id, { handle, startedAt: Date.now(), remaining: ms, paused: false })
}

/** Stops a timed toast's clock so it cannot vanish while it is being read. */
export const pauseToast = (id: string): void => {
  const running = countdowns.get(id)
  if (running === undefined || running.paused) return
  clearTimeout(running.handle)
  countdowns.set(id, {
    ...running,
    paused: true,
    remaining: Math.max(0, running.remaining - (Date.now() - running.startedAt)),
  })
}

/** Picks the clock up where `pauseToast` left it. */
export const resumeToast = (id: string): void => {
  const paused = countdowns.get(id)
  if (paused?.paused !== true) return
  armDismiss(id, paused.remaining)
}

const push = (tone: ToastTone, message: string, action?: ToastAction): string => {
  // The same failure re-raised is the same failure, not a second one. A poll
  // that fails every 5s must leave one bar standing, not twelve — so an
  // identical tone+message already on screen keeps its id and its position.
  const showing = useToastStore
    .getState()
    .toasts.find((t) => t.tone === tone && t.message === message)
  if (showing !== undefined) return showing.id

  const id = randomId()
  const item: ToastItem =
    action === undefined ? { id, tone, message } : { id, tone, message, action }
  useToastStore.setState((state) => ({ toasts: [...state.toasts, item].slice(-MAX_TOASTS) }))

  // A failure the user never saw is worse than a lingering bar: every other
  // tone clears itself, Error waits for the person to hit Dismiss/Retry.
  if (tone !== 'error') armDismiss(id, AUTO_DISMISS_MS)

  return id
}

/**
 * Plain functions, not hooks — an API error interceptor has no component
 * instance to call a hook from, so raising a toast can't require one.
 */
export const toast = {
  info: (message: string, action?: ToastAction) => push('info', message, action),
  success: (message: string, action?: ToastAction) => push('success', message, action),
  error: (message: string, action?: ToastAction) => push('error', message, action),
  offline: (message: string, action?: ToastAction) => push('offline', message, action),
}
